import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ErrorCodes, ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import type { MessageKey } from "../../i18n/dictionaries";
import { usePrepareOffers, type LineCode } from "../prepare-offers/use-prepare-offers";
import { PackagePicker } from "../prepare-offers/package-picker";
import { usePartySearch, type PartyHit } from "../household/use-household";
import {
  PRODUCT_LINES,
  ProductFields,
  emptyProductFields,
  productFieldsInvalid,
  productFieldsToPayload,
  type ProductFieldValues,
} from "../intake/product-fields";
import type { ProductLine } from "../intake/types";
import { useQuoteCapture, type ContactChannel, type QuoteCaptureResult } from "./use-quote-capture";

// MÜŞTERİ → TEKLİF FORMU — an advisor-operated, in-console mirror of the public 3-step flow
// (Bilgiler → Teklifler → Danışman). The customer arrived off any channel; the advisor captures
// them (product-first, per-product fields shared with phone-intake) and authors 1–3 offers, all
// recorded. Two entry paths: "Yeni üye" (fresh capture) and "Kayıtlı üye" (attach a campaign
// offer to an EXISTING party/household via /v1/parties/find — reuses ids, no duplicate party).
// Step 1 reuses the capture chain; Step 2 reuses POST /v1/leads/:leadId/prepare-offers with the
// shared PackagePicker; Step 3 hands off to /recommendations/:id. No new backend contracts.

// Mirrors packages/validation premiumSchema: non-negative decimal, ≤12 int / ≤2 frac.
const PREMIUM_RE = /^\d{1,12}(\.\d{1,2})?$/;
const MAX_OFFERS = 3;

// "Nereden geldi?" — the UI channel palette the founder asked for. The four traditional
// origins map 1:1 to recordInteractionSchema.channel; the three non-traditional ones (acquisition
// sources) map onto the nearest comms bucket (PORTAL = online/other) while the granular
// human-readable label is preserved on the interaction (intent subjectRef). This mapping is
// intentional and settled: BE decided acquisition-source ≠ comms-medium, so the channel enum is
// NOT extended — the label captures the source, the enum captures the medium.
const CHANNEL_OPTIONS = [
  "PHONE",
  "EMAIL",
  "WHATSAPP",
  "IN_PERSON",
  "INTERNET_AD",
  "INSTAGRAM",
  "OTHER",
] as const;
type ChannelUi = (typeof CHANNEL_OPTIONS)[number];
const DIRECT_CHANNELS = new Set<ChannelUi>(["PHONE", "EMAIL", "WHATSAPP", "IN_PERSON"]);
const CHANNEL_TO_ENUM: Record<ChannelUi, ContactChannel> = {
  PHONE: "PHONE",
  EMAIL: "EMAIL",
  WHATSAPP: "WHATSAPP",
  IN_PERSON: "IN_PERSON",
  INTERNET_AD: "PORTAL",
  INSTAGRAM: "PORTAL",
  OTHER: "PORTAL",
};
// Step-1 product line → prepare-offers line code. Sağlık has no single line code; tss is the
// nearest PERSON/HEALTH line (the advisor refines carrier/package in Step 2).
const PRODUCT_TO_LINECODE: Record<ProductLine, LineCode> = {
  trafik: "trafik",
  kasko: "kasko",
  dask: "dask",
  seyahat: "seyahat",
  saglik: "tss",
};

interface OfferDraft {
  packageCode: string;
  grossPremium: string;
}
const emptyOffer = (): OfferDraft => ({ packageCode: "", grossPremium: "" });
const premiumOk = (v: string): boolean => PREMIUM_RE.test(v.trim());

/** Turkish gloss for the prepare-offers ProblemJson detail (raw detail is shown too). */
function glossKey(detail: string): MessageKey | null {
  const d = detail.toLowerCase();
  if (d.includes("line-indeterminate")) return "po.err.lineNeeded";
  if (d.includes("asset-ambiguous")) return "po.err.assetAmbiguous";
  if (d.includes("no-insurable-asset") || d.includes("has-no-insurable-asset"))
    return "po.err.noAsset";
  if (d.includes("only-trafik-kasko")) return "po.err.unsupportedLine";
  if (d.includes("not permitted") || d.includes("not-authorized")) return "po.err.notAuthorized";
  if (d.includes("premium")) return "po.err.premium";
  if (d.includes("package")) return "po.err.package";
  return null;
}

type StepNo = 1 | 2 | 3;
const STEP_KEYS: Record<StepNo, MessageKey> = {
  1: "qf.step1",
  2: "qf.step2",
  3: "qf.step3",
};

function Stepper({ step }: { step: StepNo }) {
  const { t } = useT();
  return (
    <ol className="qf-steps" aria-label={t("qf.heading")} data-testid="qf-stepper">
      {([1, 2, 3] as StepNo[]).map((n) => {
        const state = n < step ? "done" : n === step ? "current" : "todo";
        return (
          <li
            key={n}
            className={`qf-step qf-step--${state}`}
            aria-current={n === step || undefined}
          >
            <span className="qf-step__num">
              {state === "done" ? <Icon name="check" size={14} /> : n}
            </span>
            <span className="qf-step__label">{t(STEP_KEYS[n])}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function QuoteFormPage() {
  const { t } = useT();
  const { me } = usePrincipal();
  const navigate = useNavigate();

  // The wizard crosses the capture chain (party/lead/capture) AND prepare-offers
  // (recommendation). Gate the affordance on all of them; the API enforces each call.
  const canUse =
    hasGrant(me, "lead:write", "own") &&
    hasGrant(me, "party:write", "own") &&
    hasGrant(me, "capture:write", "own") &&
    hasGrant(me, "recommendation:write", "own");

  const [step, setStep] = useState<StepNo>(1);

  // ── Entry mode — Yeni üye (fresh capture) | Kayıtlı üye (existing party/household).
  const [mode, setMode] = useState<"new" | "registered">("new");

  // ── Kayıtlı üye — party-find search (reuses GET /v1/parties/find via usePartySearch).
  const [regQuery, setRegQuery] = useState("");
  const [regSubmitted, setRegSubmitted] = useState("");
  // Auto-detect: a query starting with + or a digit is a phone; otherwise a name (U1 pattern).
  const regFindMode: "phone" | "name" = /^[+\d]/.test(regSubmitted.trim()) ? "phone" : "name";
  const regSearch = usePartySearch(regFindMode, regSubmitted);
  const regHits = regSearch.data ?? [];
  const [selectedParty, setSelectedParty] = useState<PartyHit | null>(null);

  // ── Step 1 — Bilgiler.
  const [line, setLine] = useState<ProductLine>("trafik");
  const [channelUi, setChannelUi] = useState<ChannelUi>("PHONE");
  const [channelOther, setChannelOther] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [tckn, setTckn] = useState("");
  const [fields, setFields] = useState<ProductFieldValues>(emptyProductFields);
  const patchFields = (patch: Partial<ProductFieldValues>) =>
    setFields((f) => ({ ...f, ...patch }));
  const [serviceConsent, setServiceConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [healthConsent, setHealthConsent] = useState(false);
  const capture = useQuoteCapture();
  const [captured, setCaptured] = useState<QuoteCaptureResult | null>(null);

  // ── Step 2 — Teklifler.
  const lineCode = PRODUCT_TO_LINECODE[line];
  const prepare = usePrepareOffers(captured?.leadId ?? "");
  const [offers, setOffers] = useState<OfferDraft[]>([emptyOffer()]);
  const [prepared, setPrepared] = useState<{
    recommendationId: string;
    offers: OfferDraft[];
  } | null>(null);

  if (!canUse) {
    return (
      <section className="surface">
        <div className="k-note">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--restricted">
              <Icon name="lock" size={18} />
            </span>
            <div>
              <p className="k-note__title k-note__title--sm">{t("qf.heading")}</p>
              <p role="note" className="k-note__desc" data-testid="qf-restricted">
                {t("qf.restricted")}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const usingRegistered = mode === "registered";
  const partySelected = usingRegistered && !!selectedParty;
  // The full Step-1 form appears for a fresh capture, or once a registered member is chosen.
  const showForm = !usingRegistered || partySelected;

  // Effective identity — from the found party on the registered path, else the typed inputs.
  const effFirst = usingRegistered ? (selectedParty?.firstName ?? "") : firstName;
  const effLast = usingRegistered ? (selectedParty?.lastName ?? "") : lastName;
  const effPhone = usingRegistered ? (selectedParty?.phone ?? "") : phone;

  const isSaglik = line === "saglik";
  const channelOtherOk = channelUi !== "OTHER" || channelOther.trim().length > 0;
  const productOk = !productFieldsInvalid(line, fields) && (!isSaglik || healthConsent);
  const commonOk = serviceConsent && channelOtherOk && productOk;
  const step1Ready = usingRegistered
    ? Boolean(selectedParty?.householdId) && commonOk
    : firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      phone.trim().length > 0 &&
      tckn.trim().length === 11 &&
      commonOk;

  const channelLabel = (c: ChannelUi): string =>
    c === "OTHER" && channelOther.trim() ? channelOther.trim() : t(`qf.channel.${c}` as MessageKey);

  const submitStep1 = async () => {
    if (!step1Ready || capture.isPending) return;
    const channelDetail = DIRECT_CHANNELS.has(channelUi)
      ? undefined
      : { sourceChannel: channelUi, sourceLabel: channelLabel(channelUi) };
    try {
      const res = await capture.run({
        line,
        channel: CHANNEL_TO_ENUM[channelUi],
        ...(channelDetail ? { channelDetail } : {}),
        firstName: effFirst,
        lastName: effLast,
        phone: effPhone,
        email: usingRegistered ? undefined : email.trim() || undefined,
        tckn: usingRegistered ? undefined : tckn,
        ...productFieldsToPayload(line, fields),
        serviceConsent,
        marketingConsent,
        // Sağlık line: the SEPARATE health açık rıza attestation rides the interaction metadata.
        healthConsent: isSaglik ? healthConsent : undefined,
        ...(usingRegistered && selectedParty
          ? {
              existingPartyId: selectedParty.id,
              ...(selectedParty.householdId
                ? { existingHouseholdId: selectedParty.householdId }
                : {}),
            }
          : {}),
      });
      setCaptured(res);
      setStep(2);
    } catch {
      // Error is surfaced from capture.error; the progress ref lets the advisor retry
      // the failing call without re-creating the party.
    }
  };

  // ── Step 2 helpers.
  const patch = (i: number, next: Partial<OfferDraft>) =>
    setOffers((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...next } : o)));
  const addRow = () =>
    setOffers((prev) => (prev.length < MAX_OFFERS ? [...prev, emptyOffer()] : prev));
  const removeRow = (i: number) =>
    setOffers((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  const started = offers.filter((o) => o.packageCode.trim() !== "");
  const step2Ready =
    started.length >= 1 && started.every((o) => premiumOk(o.grossPremium)) && !prepare.isPending;

  const submitStep2 = () => {
    if (!step2Ready || !captured) return;
    prepare.mutate(
      {
        lineCode,
        ...(captured.assetId ? { insuredAssetId: captured.assetId } : {}),
        offers: started.map((o) => ({
          packageCode: o.packageCode.trim(),
          grossPremium: o.grossPremium.trim(),
          currency: "TRY" as const,
        })),
      },
      {
        onSuccess: (res) => {
          setPrepared({ recommendationId: res.recommendationId, offers: started });
          setStep(3);
        },
      },
    );
  };

  const captureProblem = capture.error instanceof ProblemError ? capture.error : null;
  const prepareProblem = prepare.error instanceof ProblemError ? prepare.error.problem : null;
  const glossed = prepareProblem ? glossKey(prepareProblem.detail) : null;

  const maskedTckn =
    !usingRegistered && tckn.length === 11
      ? `${tckn.slice(0, 3)}••••••${tckn.slice(-2)}`
      : t("qf.reg.masked");

  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("qf.heading")}</h1>
          <p className="surface__subtitle">{t("qf.subheading")}</p>
        </div>
      </header>

      <Stepper step={step} />

      {/* ─────────────── STEP 1 — Bilgiler ─────────────── */}
      {/* The wizard step containers are plain <div>s (not <form>s): Step 1 holds the
          Kayıtlı-üye search <form>, so a wrapping step <form> would nest forms (an invalid
          validateDOMNesting <form>-in-<form>). Each "Devam"/"Hazırla" button drives its step
          via onClick — no implicit form submit — while the search stays its own real <form>. */}
      {step === 1 && (
        <div className="qf-form" data-testid="qf-step1">
          {/* Yeni üye | Kayıtlı üye — the entry toggle. */}
          <div
            className="qf-seg"
            role="tablist"
            aria-label={t("qf.modeLabel")}
            data-testid="qf-mode"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "new"}
              className={`qf-seg__btn${mode === "new" ? " qf-seg__btn--active" : ""}`}
              data-testid="qf-mode-new"
              onClick={() => setMode("new")}
            >
              <Icon name="member" size={15} />
              {t("qf.mode.new")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "registered"}
              className={`qf-seg__btn${mode === "registered" ? " qf-seg__btn--active" : ""}`}
              data-testid="qf-mode-registered"
              onClick={() => setMode("registered")}
            >
              <Icon name="search" size={15} />
              {t("qf.mode.registered")}
            </button>
          </div>

          {/* Kayıtlı üye — find an existing party (name/TCKN-adjacent phone) and autofill. */}
          {usingRegistered && (
            <div className="k-card" data-testid="qf-reg">
              <p className="surface__section-label" style={{ margin: "0 0 12px" }}>
                {t("qf.reg.title")}
              </p>
              {!selectedParty ? (
                <>
                  <form
                    style={{ display: "flex", gap: 10, alignItems: "stretch" }}
                    onSubmit={(ev) => {
                      ev.preventDefault();
                      setRegSubmitted(regQuery);
                    }}
                  >
                    <label className="k-search" style={{ flex: 1 }}>
                      <Icon name="search" size={18} />
                      <input
                        aria-label={t("qf.reg.searchLabel")}
                        data-testid="qf-reg-input"
                        placeholder={t("qf.reg.placeholder")}
                        value={regQuery}
                        onChange={(ev) => setRegQuery(ev.target.value)}
                      />
                    </label>
                    <button
                      type="submit"
                      className="k-btn k-btn--secondary"
                      data-testid="qf-reg-search"
                      disabled={!regQuery.trim()}
                    >
                      {t("qf.reg.go")}
                    </button>
                  </form>
                  {regSubmitted.trim() ? (
                    <div className="qf-reg-results" data-testid="qf-reg-results">
                      {regSearch.isLoading ? (
                        <p className="k-field__hint">{t("qf.reg.loading")}</p>
                      ) : null}
                      {!regSearch.isLoading && (regSearch.error || regHits.length === 0) ? (
                        <div className="k-empty" data-testid="qf-reg-none">
                          <div className="k-empty__icon">
                            <Icon name="search" size={26} />
                          </div>
                          <p className="k-empty__title">{t("qf.reg.none")}</p>
                        </div>
                      ) : (
                        <ul
                          style={{
                            listStyle: "none",
                            margin: "12px 0 0",
                            padding: 0,
                            display: "grid",
                            gap: 8,
                          }}
                        >
                          {regHits.map((h) => {
                            const name = `${h.firstName} ${h.lastName}`.trim();
                            const canPick = Boolean(h.householdId);
                            return (
                              <li key={h.id} className="k-card k-card--pad-sm">
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span className="k-avatar">
                                    {(h.firstName?.[0] ?? "") + (h.lastName?.[0] ?? "")}
                                  </span>
                                  <span style={{ fontWeight: 600, color: "var(--k-ink)" }}>
                                    {name}
                                  </span>
                                  {h.phone ? (
                                    <span style={{ fontSize: 12.5, color: "var(--k-muted)" }}>
                                      {" "}
                                      · {h.phone}
                                    </span>
                                  ) : null}
                                  {canPick ? (
                                    <button
                                      type="button"
                                      className="k-btn k-btn--primary k-btn--sm"
                                      style={{ marginLeft: "auto" }}
                                      data-testid={`qf-reg-pick-${h.id}`}
                                      onClick={() => setSelectedParty(h)}
                                    >
                                      {t("qf.reg.select")}
                                    </button>
                                  ) : (
                                    // No householdId on this hit → the interaction/consent can't be
                                    // attached; can't safely reuse it yet (mirrors U1-F). Flag it.
                                    <span
                                      className="k-badge k-badge--warning"
                                      style={{ marginLeft: "auto" }}
                                      data-testid={`qf-reg-nohh-${h.id}`}
                                    >
                                      {t("qf.reg.noHousehold")}
                                    </span>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </>
              ) : (
                <div
                  className="qf-reg-selected"
                  data-testid="qf-reg-selected"
                  style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
                >
                  <span className="k-avatar">
                    {(selectedParty.firstName?.[0] ?? "") + (selectedParty.lastName?.[0] ?? "")}
                  </span>
                  <div>
                    <p style={{ fontWeight: 600, margin: 0 }}>
                      {`${selectedParty.firstName} ${selectedParty.lastName}`.trim()}
                    </p>
                    <p className="k-field__hint" style={{ margin: 0 }}>
                      {selectedParty.phone ?? t("qf.reg.noPhone")} · {t("qf.reg.attach")}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="k-btn k-btn--ghost k-btn--sm"
                    style={{ marginLeft: "auto" }}
                    data-testid="qf-reg-change"
                    onClick={() => setSelectedParty(null)}
                  >
                    {t("qf.reg.change")}
                  </button>
                </div>
              )}
              <p className="k-field__hint" data-testid="qf-reg-note">
                {t("qf.reg.note")}
              </p>
            </div>
          )}

          {showForm && (
            <>
              <div className="k-card">
                <p className="surface__section-label" style={{ margin: "0 0 16px" }}>
                  {t("qf.section.product")}
                </p>
                <label className="k-field">
                  <span className="k-field__label">{t("qf.line")}</span>
                  <span className="k-select">
                    <select
                      className="k-select__native"
                      data-testid="qf-line"
                      value={line}
                      onChange={(e) => setLine(e.target.value as ProductLine)}
                    >
                      {PRODUCT_LINES.map((code) => (
                        <option key={code} value={code}>
                          {t(`qf.line.${code}` as MessageKey)}
                        </option>
                      ))}
                    </select>
                    <span className="k-select__chevron">
                      <Icon name="chevron-down" size={16} />
                    </span>
                  </span>
                </label>
                <label className="k-field">
                  <span className="k-field__label">{t("qf.channel")}</span>
                  <span className="k-select">
                    <select
                      className="k-select__native"
                      data-testid="qf-channel"
                      value={channelUi}
                      onChange={(e) => setChannelUi(e.target.value as ChannelUi)}
                    >
                      {CHANNEL_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {t(`qf.channel.${c}` as MessageKey)}
                        </option>
                      ))}
                    </select>
                    <span className="k-select__chevron">
                      <Icon name="chevron-down" size={16} />
                    </span>
                  </span>
                  <span className="k-field__hint">{t("qf.channelHint")}</span>
                </label>
                {channelUi === "OTHER" ? (
                  <label className="k-field">
                    <span className="k-field__label">{t("qf.channelOther")}</span>
                    <input
                      className="k-input"
                      data-testid="qf-channel-other"
                      value={channelOther}
                      placeholder={t("qf.channelOtherPlaceholder")}
                      onChange={(e) => setChannelOther(e.target.value)}
                    />
                  </label>
                ) : null}
              </div>

              <div className="k-card">
                <p className="surface__section-label" style={{ margin: "0 0 16px" }}>
                  {t("qf.section.customer")}
                </p>
                {usingRegistered ? (
                  <>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.firstName")}</span>
                      <input className="k-input" data-testid="qf-first" value={effFirst} readOnly />
                    </label>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.lastName")}</span>
                      <input className="k-input" data-testid="qf-last" value={effLast} readOnly />
                    </label>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.tckn")}</span>
                      <input
                        className="k-input"
                        data-testid="qf-tckn"
                        value=""
                        placeholder={t("qf.reg.masked")}
                        readOnly
                      />
                      <span className="k-field__hint" data-testid="qf-reg-tckn-flag">
                        {t("qf.reg.tcknFlag")}
                      </span>
                    </label>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.phone")}</span>
                      <input className="k-input" data-testid="qf-phone" value={effPhone} readOnly />
                      {!effPhone ? (
                        <span className="k-field__hint">{t("qf.reg.noPhone")}</span>
                      ) : null}
                    </label>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.email")}</span>
                      <input className="k-input" data-testid="qf-email" value="" readOnly />
                      <span className="k-field__hint">{t("qf.reg.emailFlag")}</span>
                    </label>
                  </>
                ) : (
                  <>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.firstName")}</span>
                      <input
                        className="k-input"
                        data-testid="qf-first"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </label>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.lastName")}</span>
                      <input
                        className="k-input"
                        data-testid="qf-last"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </label>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.tckn")}</span>
                      <input
                        className="k-input"
                        data-testid="qf-tckn"
                        inputMode="numeric"
                        value={tckn}
                        onChange={(e) => setTckn(e.target.value.replace(/\D/g, "").slice(0, 11))}
                      />
                    </label>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.phone")}</span>
                      <input
                        className="k-input"
                        data-testid="qf-phone"
                        inputMode="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </label>
                    <label className="k-field">
                      <span className="k-field__label">{t("qf.email")}</span>
                      <input
                        className="k-input"
                        data-testid="qf-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </label>
                  </>
                )}
              </div>

              {/* Product-aware field-set — shared with phone-intake (canonical [KAPI] subset). */}
              <ProductFields line={line} values={fields} onChange={patchFields} />

              <div className="k-card k-card--accent-amber" data-testid="qf-consent">
                <div className="k-note__row" style={{ marginBottom: 16 }}>
                  <span className="k-note__icon k-note__icon--restricted">
                    <Icon name="lock" size={18} />
                  </span>
                  <div>
                    <p className="k-note__title k-note__title--sm">{t("qf.section.consent")}</p>
                    <p className="k-note__desc">{t("qf.noticeRef")}</p>
                  </div>
                </div>
                <label className="k-choice" style={{ marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    data-testid="qf-consent-service"
                    checked={serviceConsent}
                    onChange={(e) => setServiceConsent(e.target.checked)}
                  />
                  <span className="k-check">
                    <Icon name="check" size={14} />
                  </span>
                  {t("qf.serviceAttest")}
                </label>
                <label className="k-choice" style={{ marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    data-testid="qf-consent-marketing"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                  />
                  <span className="k-check">
                    <Icon name="check" size={14} />
                  </span>
                  {t("qf.marketingAttest")}
                </label>
                {isSaglik ? (
                  <>
                    <label className="k-choice" style={{ marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        data-testid="qf-consent-health"
                        checked={healthConsent}
                        onChange={(e) => setHealthConsent(e.target.checked)}
                      />
                      <span className="k-check">
                        <Icon name="check" size={14} />
                      </span>
                      {t("intake.healthAttest")}
                    </label>
                    <p className="k-field__hint" data-testid="qf-health-consent-note">
                      {t("intake.healthConsentNote")}
                    </p>
                  </>
                ) : null}
                <p className="k-field__hint">{t("qf.iysNote")}</p>
              </div>
            </>
          )}

          {/* Real ProblemJson errors — a dedup 409 becomes a helpful next step. */}
          {captureProblem && captureProblem.is(ErrorCodes.PARTY_ALREADY_EXISTS) ? (
            <div role="note" className="k-note" data-testid="qf-dedup">
              <div className="k-note__row">
                <span className="k-note__icon k-note__icon--restricted">
                  <Icon name="info" size={18} />
                </span>
                <div>
                  <p className="k-note__desc">{t("qf.dedup")}</p>
                  <div className="k-note__actions">
                    <button
                      type="button"
                      className="k-btn k-btn--secondary k-btn--sm"
                      data-testid="qf-dedup-registered"
                      onClick={() => setMode("registered")}
                    >
                      <Icon name="search" size={15} />
                      {t("qf.dedupRegistered")}
                    </button>
                    <Link
                      to="/households"
                      className="k-btn k-btn--ghost k-btn--sm"
                      data-testid="qf-dedup-search"
                    >
                      {t("qf.dedupSearch")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : capture.error ? (
            <p role="alert" className="k-field__error" data-testid="qf-capture-error">
              <Icon name="alert-triangle" size={14} />
              {captureProblem ? captureProblem.problem.detail : t("qf.captureError")}
            </p>
          ) : null}

          {showForm && (
            <div className="qf-actions">
              {/* GAP-AUDIT-3 D4 — once the capture has SUCCEEDED, Step 1 is done: the
                  party, household, asset, lead and interaction all exist. The capture
                  chain is deliberately resumable (a progress ref, so a retry never
                  re-creates the party and trips CSA-1009), which meant a SECOND run
                  after success skipped every step and returned the same ids —
                  pressing "Devam" again fired zero requests, silently discarded any
                  edits, and advanced the wizard as if they had been saved. There is
                  no endpoint to amend a captured party from here, so the honest move
                  is to stop pretending Step 1 is still editable: it becomes a
                  read-only "saved" state that continues forward. Corrections belong
                  on the customer record. */}
              {captured ? (
                <>
                  <button
                    type="button"
                    className="k-btn k-btn--primary"
                    data-testid="qf-step1-continue"
                    onClick={() => setStep(2)}
                  >
                    <Icon name="arrow-right" size={16} />
                    {t("qf.continue")}
                  </button>
                  <p className="k-field__hint" data-testid="qf-step1-locked">
                    {t("qf.step1Locked")}
                  </p>
                </>
              ) : (
                <button
                  type="button"
                  className="k-btn k-btn--primary"
                  data-testid="qf-step1-continue"
                  disabled={!step1Ready || capture.isPending}
                  onClick={submitStep1}
                >
                  <Icon name="arrow-right" size={16} />
                  {capture.isPending ? t("qf.saving") : t("qf.continue")}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─────────────── STEP 2 — Teklifler ─────────────── */}
      {step === 2 && captured && (
        <div className="qf-form" data-testid="qf-step2">
          <div className="k-card">
            <p className="surface__section-label" style={{ margin: "0 0 6px" }}>
              {t("qf.offersTitle")}
            </p>
            <p className="k-field__hint" data-testid="qf-catalog-flag" style={{ marginBottom: 12 }}>
              <Icon name="info" size={14} /> {t("po.catalogFlag")}
            </p>

            <div className="po-offers">
              {offers.map((o, i) => {
                const premiumInvalid = o.grossPremium.trim() !== "" && !premiumOk(o.grossPremium);
                return (
                  <div className="po-offer-row" data-testid="qf-offer-row" key={i}>
                    <span className="k-counter po-row-index">{i + 1}</span>
                    <label className="k-field po-package">
                      <span className="k-field__label">{t("po.packageCode")}</span>
                      {/* Shared "Carrier — Package" picker (#177); degrades to free-text. */}
                      <PackagePicker
                        lineCode={lineCode}
                        value={o.packageCode}
                        ariaLabel={`${t("po.packageCode")} ${i + 1}`}
                        disabled={prepare.isPending}
                        onChange={(packageCode) => patch(i, { packageCode })}
                      />
                    </label>
                    <label className="k-field po-premium">
                      <span className="k-field__label">{t("po.premium")}</span>
                      <input
                        className={`k-input${premiumInvalid ? " k-input--error" : ""}`}
                        aria-label={`${t("po.premium")} ${i + 1}`}
                        inputMode="decimal"
                        value={o.grossPremium}
                        placeholder="0.00"
                        aria-invalid={premiumInvalid}
                        onChange={(e) => patch(i, { grossPremium: e.target.value })}
                      />
                      {premiumInvalid && (
                        <span role="alert" className="k-field__error">
                          <Icon name="alert-triangle" size={14} />
                          {t("po.premiumInvalid")}
                        </span>
                      )}
                    </label>
                    {offers.length > 1 && (
                      <button
                        type="button"
                        className="k-btn k-btn--ghost k-btn--sm"
                        data-testid="qf-remove-offer"
                        disabled={prepare.isPending}
                        onClick={() => removeRow(i)}
                      >
                        <Icon name="trash" size={15} />
                        {t("po.removeOffer")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="k-field__hint">{t("po.packageHint")}</p>

            <div className="po-actions">
              <button
                type="button"
                className="k-btn k-btn--secondary k-btn--sm"
                data-testid="qf-add-offer"
                disabled={offers.length >= MAX_OFFERS || prepare.isPending}
                onClick={addRow}
              >
                <Icon name="plus" size={15} />
                {t("po.addOffer")}
              </button>
            </div>

            {started.length === 0 && <p className="k-field__hint">{t("po.needOne")}</p>}

            {prepareProblem && (
              <p role="alert" className="k-field__error" data-testid="qf-prepare-error">
                <Icon name="alert-triangle" size={14} />
                <span>
                  {glossed ? t(glossed) : prepareProblem.detail}
                  {glossed && <span className="k-field__hint"> — {prepareProblem.detail}</span>}
                </span>
              </p>
            )}
          </div>

          <div className="qf-actions">
            <button
              type="button"
              className="k-btn k-btn--ghost"
              data-testid="qf-step2-back"
              disabled={prepare.isPending}
              onClick={() => setStep(1)}
            >
              <Icon name="chevron-left" size={16} />
              {t("qf.back")}
            </button>
            <button
              type="button"
              className="k-btn k-btn--primary"
              data-testid="qf-step2-continue"
              disabled={!step2Ready}
              onClick={submitStep2}
            >
              <Icon name="arrow-right" size={16} />
              {prepare.isPending ? t("po.submitting") : t("qf.continue")}
            </button>
          </div>
        </div>
      )}

      {/* ─────────────── STEP 3 — Danışman ─────────────── */}
      {step === 3 && captured && prepared && (
        <div className="qf-form" data-testid="qf-step3">
          <div className="k-card">
            <span className="k-badge k-badge--success">
              <Icon name="check" size={13} />
              {t("qf.doneBadge")}
            </span>
            <h2 className="k-note__title" style={{ marginTop: 10 }}>
              {t("qf.summaryTitle")}
            </h2>

            <p className="surface__section-label" style={{ margin: "16px 0 8px" }}>
              {t("qf.summaryCustomer")}
            </p>
            <dl className="qf-summary">
              <div>
                <dt>
                  {t("qf.firstName")} / {t("qf.lastName")}
                </dt>
                <dd data-testid="qf-summary-name">
                  {effFirst} {effLast}
                </dd>
              </div>
              <div>
                <dt>{t("qf.tckn")}</dt>
                <dd>{maskedTckn}</dd>
              </div>
              <div>
                <dt>{t("qf.phone")}</dt>
                <dd>{effPhone || "—"}</dd>
              </div>
              <div>
                <dt>{t("qf.line")}</dt>
                <dd>{t(`qf.line.${line}` as MessageKey)}</dd>
              </div>
              <div>
                <dt>{t("qf.channel")}</dt>
                <dd data-testid="qf-summary-channel">{channelLabel(channelUi)}</dd>
              </div>
              <div>
                <dt>{t("qf.leadRef")}</dt>
                <dd>{captured.leadId.slice(0, 8)}</dd>
              </div>
            </dl>
            {captured.assetError && (
              <p role="alert" className="k-field__error" data-testid="qf-asset-error">
                <Icon name="alert-triangle" size={14} />
                {t("qf.assetError")}
              </p>
            )}

            <p className="surface__section-label" style={{ margin: "16px 0 8px" }}>
              {t("qf.summaryOffers")}
            </p>
            <ul className="qf-offer-list" data-testid="qf-summary-offers">
              {prepared.offers.map((o, i) => (
                <li key={i}>
                  <span className="k-chip">{o.packageCode}</span>
                  <span>{o.grossPremium} TRY</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="qf-actions">
            <button
              type="button"
              className="k-btn k-btn--primary"
              data-testid="qf-goto-recommendation"
              onClick={() => navigate(`/recommendations/${prepared.recommendationId}`)}
            >
              <Icon name="arrow-right" size={16} />
              {t("qf.goToRecommendation")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
